import { useEffect, useState } from 'react';
import { Check, ExternalLink, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import LogoReseau from './LogoReseau';

export type LigneReseau = { reseau: string; url: string };
export type EntreeCatalogue = { cle: string; label: string; exemple: string };

/**
 * Liste de comptes sur les réseaux, en saisie.
 *
 * Le comité et ses sponsors tiennent la même liste, avec les mêmes règles : le
 * composant ne connaît donc ni l'un ni l'autre, il reçoit l'état et rend
 * l'état suivant. Chaque validation enregistre la liste entière — un échec ne
 * laisse jamais la moitié des liens écrits.
 */
export default function EditeurReseaux({
  reseaux,
  catalogue,
  onEnregistrer,
  enCours,
  messageVide,
}: {
  reseaux: LigneReseau[];
  catalogue: EntreeCatalogue[];
  onEnregistrer: (liste: LigneReseau[]) => void;
  enCours: boolean;
  messageVide: string;
}) {
  const [lignes, setLignes] = useState<LigneReseau[]>(reseaux);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [brouillon, setBrouillon] = useState<LigneReseau>({ reseau: '', url: '' });
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setLignes(reseaux);
  }, [reseaux]);

  /** Réseaux encore disponibles : un même réseau ne se met pas deux fois. */
  const disponibles = catalogue.filter(
    (c) => !lignes.some((l, i) => l.reseau === c.cle && i !== enEdition)
  );

  const valider = () => {
    if (brouillon.reseau === '' || brouillon.url.trim() === '') {
      setErreur('Choisissez un réseau et saisissez son lien.');
      return;
    }

    const suite =
      enEdition === null
        ? [...lignes, brouillon]
        : lignes.map((l, i) => (i === enEdition ? brouillon : l));

    setLignes(suite);
    setEnEdition(null);
    setBrouillon({ reseau: '', url: '' });
    setErreur(null);
    onEnregistrer(suite);
  };

  const supprimer = (index: number) => {
    const suite = lignes.filter((_, i) => i !== index);
    setLignes(suite);
    if (enEdition === index) {
      setEnEdition(null);
      setBrouillon({ reseau: '', url: '' });
    }
    onEnregistrer(suite);
  };

  const saisieOuverte = enEdition !== null || brouillon.reseau !== '';

  return (
    <div className="space-y-3">
      {erreur && <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">{erreur}</p>}

      {lignes.length === 0 && !saisieOuverte && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{messageVide}</p>
      )}

      <div className="space-y-2">
        {lignes.map((ligne, index) =>
          enEdition === index ? (
            <Saisie
              key={`edition-${index}`}
              catalogue={disponibles}
              tousLesReseaux={catalogue}
              valeur={brouillon}
              onChange={setBrouillon}
              onValider={valider}
              onAnnuler={() => {
                setEnEdition(null);
                setBrouillon({ reseau: '', url: '' });
                setErreur(null);
              }}
              enCours={enCours}
            />
          ) : (
            <div
              key={`${ligne.reseau}-${index}`}
              className="flex items-center gap-3 rounded-xl border border-gray-100 dark:border-gray-800 px-3 py-2.5"
            >
              <LogoReseau reseau={ligne.reseau} taille={22} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {catalogue.find((c) => c.cle === ligne.reseau)?.label ?? ligne.reseau}
                </p>
                <a
                  href={ligne.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-gray-500 dark:text-gray-400 hover:underline inline-flex items-center gap-1 break-all"
                >
                  {ligne.url}
                  <ExternalLink className="w-3 h-3 shrink-0" />
                </a>
              </div>

              <button
                type="button"
                onClick={() => {
                  setEnEdition(index);
                  setBrouillon(ligne);
                  setErreur(null);
                }}
                title="Modifier ce réseau"
                aria-label="Modifier ce réseau"
                className="p-1.5 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700 shrink-0"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => supprimer(index)}
                disabled={enCours}
                title="Retirer ce réseau"
                aria-label="Retirer ce réseau"
                className="p-1.5 rounded-lg text-gray-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700 shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )
        )}

        {enEdition === null && brouillon.reseau !== '' && (
          <Saisie
            catalogue={disponibles}
            tousLesReseaux={catalogue}
            valeur={brouillon}
            onChange={setBrouillon}
            onValider={valider}
            onAnnuler={() => {
              setBrouillon({ reseau: '', url: '' });
              setErreur(null);
            }}
            enCours={enCours}
          />
        )}
      </div>

      {!saisieOuverte && disponibles.length > 0 && (
        <button
          type="button"
          onClick={() => setBrouillon({ reseau: disponibles[0].cle, url: '' })}
          className="btn-outline inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Ajouter un réseau
        </button>
      )}

      {!saisieOuverte && disponibles.length === 0 && lignes.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Tous les réseaux proposés sont déjà renseignés.
        </p>
      )}
    </div>
  );
}

/**
 * Une ligne en cours de saisie : le réseau, son lien, et le bouton qui valide.
 */
function Saisie({
  catalogue,
  tousLesReseaux,
  valeur,
  onChange,
  onValider,
  onAnnuler,
  enCours,
}: {
  catalogue: EntreeCatalogue[];
  tousLesReseaux: EntreeCatalogue[];
  valeur: LigneReseau;
  onChange: (v: LigneReseau) => void;
  onValider: () => void;
  onAnnuler: () => void;
  enCours: boolean;
}) {
  // L'exemple se cherche dans le catalogue entier : le réseau en cours de
  // modification ne figure pas parmi les disponibles, puisqu'il est déjà pris.
  const exemple =
    tousLesReseaux.find((c) => c.cle === valeur.reseau)?.exemple ??
    'https://www.facebook.com/comitesaintgenies/';

  return (
    <div className="rounded-xl border-2 border-primary-200 dark:border-primary-800 p-3 space-y-2">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex items-center gap-2 sm:w-52 shrink-0">
          <LogoReseau reseau={valeur.reseau} taille={22} />
          <select
            className="input"
            value={valeur.reseau}
            onChange={(e) => onChange({ ...valeur, reseau: e.target.value })}
            aria-label="Réseau"
          >
            {catalogue.map((c) => (
              <option key={c.cle} value={c.cle}>
                {c.label}
              </option>
            ))}
            {/* Le réseau en cours d'édition n'est pas dans les disponibles :
                sans cette option, le select afficherait une valeur absente. */}
            {!catalogue.some((c) => c.cle === valeur.reseau) && valeur.reseau !== '' && (
              <option value={valeur.reseau}>
                {tousLesReseaux.find((c) => c.cle === valeur.reseau)?.label ?? valeur.reseau}
              </option>
            )}
          </select>
        </div>

        <input
          className="input"
          type="url"
          inputMode="url"
          placeholder={exemple}
          value={valeur.url}
          onChange={(e) => onChange({ ...valeur, url: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onValider();
            }
          }}
        />
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onValider}
          disabled={enCours}
          className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50"
        >
          {enCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          OK
        </button>
        <button
          type="button"
          onClick={onAnnuler}
          className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400"
        >
          <X className="w-4 h-4" />
          Annuler
        </button>
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
          Le « https:// » est ajouté tout seul.
        </span>
      </div>
    </div>
  );
}
